/**
 * CompanyField Component (ORCH-410 §4.2; ORCH-398)
 *
 * Purpose: The one Company picker: who a person works for in a tenant. Company is
 *          a field of the `user_tenant` model (saved to the membership, never the
 *          account), so this is a one-field MetadataForm over that model, limited
 *          to the Companies the tenant offers (GET /users/company-options: its
 *          enabled and tenant-local agencies and advertisers) and grouped under
 *          the Agencies / Advertisers headings every grouped list shares. Used by
 *          MembershipForm (the invite and Add to a Tenant dialogs) and placed in
 *          the Profile card by the user page and My Profile.
 * Logic: Sits in a PickerTenant (the tenant being managed) and passes the permission
 *        being exercised to its form as `pickerPermission` (ORCH-366: the layout is
 *        mode="edit", so the `user_tenant` default would be user.edit, but an invite
 *        exercises user.create), like the rest of the membership. The
 *        offered ids go to the picker through PickerValues; the server checks the
 *        same list on save. For one of Orchestrate's own people the server fixes
 *        OrchestrateHQ (`locked`), so the picker is read-only. Reports the value
 *        upward on every change; the parent decides when to save.
 * Dependencies: MetadataForm, ListFieldRenderer (groupBy), PickerScopeContext,
 *               userManagementService.companyOptions
 */

import React, { useEffect, useMemo, useState } from 'react';
import { MetadataForm } from '../metadata-form';
import { ListFieldRenderer } from '../metadata-form/FieldRenderers';
import type { FieldRendererProps } from '../metadata-form/types';
import { PickerTenant, PickerValues } from '../../context/PickerScopeContext';
import { PermissionKey } from '../../constants/permissionKeys';
import userManagementService from '../../services/userManagementService';
import type { CompanyOption } from '../../types/userManagement';
import type { MasterDataValue } from '../../types/mdm';
import { COMPANY_FIELD, MEMBERSHIP_MODEL_ID } from './membership';

export const AGENCIES = 'Agencies';
export const ADVERTISERS = 'Advertisers';

export interface CompanyFieldProps {
  /** The tenant the membership is in: its Companies and its field are offered. */
  tenantId: string;
  /** The other `user_tenant` fields, left to the Access Rights form. */
  attributeFieldIds: string[];
  /** The stored Company the form starts from; a new value resets the form. */
  initialValue: unknown;
  value: unknown;
  onChange: (companyId: unknown) => void;
  /** The permission being exercised: `user.edit` on a profile, `user.create` on an invite. */
  permission: PermissionKey;
  readOnly?: boolean;
  /** The server fixes OrchestrateHQ for one of Orchestrate's own people: shown, not editable. */
  locked?: boolean;
}

/** The heading an offered Company goes under, from the server's group. */
export const companyGroup = (
  options: CompanyOption[] | null
): ((option: MasterDataValue) => string) => {
  const byId = new Map((options ?? []).map((o) => [o.id, o.group]));
  return (option) => {
    const group = byId.get(option.id) ?? byId.get(option.global_value_id || '');
    return group === 'agency' ? AGENCIES : group === 'advertiser' ? ADVERTISERS : '';
  };
};

const CompanyField: React.FC<CompanyFieldProps> = ({
  tenantId,
  attributeFieldIds,
  initialValue,
  value,
  onChange,
  permission,
  readOnly = false,
  locked = false,
}) => {
  // Until the list arrives, and if it can't be read, the picker offers nothing new
  const [options, setOptions] = useState<CompanyOption[] | null>(null);
  useEffect(() => {
    let current = true;
    setOptions(null);
    userManagementService
      .companyOptions(tenantId)
      .then((list) => current && setOptions(list))
      .catch(() => current && setOptions([]));
    return () => {
      current = false;
    };
  }, [tenantId]);

  const limits = useMemo(
    () => ({ [COMPANY_FIELD]: (options ?? []).map((option) => option.id) }),
    [options]
  );
  const groupBy = useMemo(() => companyGroup(options), [options]);
  // The list renderer, grouped Agencies then Advertisers under the shared heading
  const customRenderers = useMemo(
    () => ({
      [COMPANY_FIELD]: (props: FieldRendererProps) => (
        <ListFieldRenderer
          {...props}
          groupBy={groupBy}
          groupOrder={[AGENCIES, ADVERTISERS]}
          disabled={props.disabled || locked}
        />
      ),
    }),
    [groupBy, locked]
  );
  // MetadataForm resets whenever `initialData` changes identity
  const initialData = useMemo(
    () => ({ metadata: { [COMPANY_FIELD]: initialValue ?? '' } }),
    [initialValue]
  );

  return (
    <PickerTenant tenantId={tenantId}>
      <PickerValues values={limits}>
        <div data-testid="company-field">
          <MetadataForm
            modelId={MEMBERSHIP_MODEL_ID}
            tenantId={tenantId}
            mode="edit"
            initialData={initialData}
            excludeFields={attributeFieldIds}
            customRenderers={customRenderers}
            onChange={(current) => {
              const next = current.metadata[COMPANY_FIELD];
              if (JSON.stringify(next ?? null) !== JSON.stringify(value ?? null)) onChange(next);
            }}
            sectionTitle=""
            showSectionDivider={false}
            columnLayout="single"
            readOnly={readOnly || locked}
            pickerPermission={permission}
          />
        </div>
      </PickerValues>
    </PickerTenant>
  );
};

export default CompanyField;
